// src/api/api.js
import axios from 'axios';
import { toast } from 'react-toastify';

const SESSION_KEY = 'hschool.session';
const TOKEN_KEY = 'hschool.token';

const readJSON = (raw, fallback = null) => {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[api] Failed to parse stored JSON', err);
    return fallback;
  }
};

const readSession = () => {
  if (typeof window === 'undefined') return null;
  try {
    return readJSON(window.localStorage.getItem(SESSION_KEY));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[api] Failed to read session', err);
    return null;
  }
};

const readStoredToken = () => {
  if (typeof window === 'undefined') return null;
  try {
    const explicit = window.localStorage.getItem(TOKEN_KEY);
    if (explicit) return explicit;
    return window.sessionStorage.getItem('token') || window.localStorage.getItem('token') || null;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[api] Failed to read token', err);
    return null;
  }
};

const clearStoredAuth = () => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(SESSION_KEY);
    window.localStorage.removeItem(TOKEN_KEY);
    window.localStorage.removeItem('token');
    window.localStorage.removeItem('user');
    window.sessionStorage.removeItem('token');
    window.dispatchEvent(new Event('auth:cleared'));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[api] Failed to clear stored auth', err);
  }
};

const getActiveToken = () => {
  const token = readStoredToken();
  const session = readSession();
  const expNumber = session?.tokenExpiresAt ? Number(session.tokenExpiresAt) : null;
  const expiresAt = Number.isFinite(expNumber) ? expNumber : null;
  return { token, expiresAt };
};

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5001/api',
  timeout: 15000,
});

instance.interceptors.request.use(
 (config) => {
    const { token, expiresAt } = getActiveToken();
    config.headers = config.headers ?? {};

    if (token) {
      if (expiresAt && expiresAt <= Date.now()) {
        clearStoredAuth();
      } else {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    if (config.data instanceof FormData) {
      delete config.headers?.['Content-Type'];
      delete config.headers?.['content-type'];
    } else if (config.headers && !config.headers['Content-Type'] && !config.headers['content-type']) {
      config.headers['Content-Type'] = 'application/json';
    }

    return config;
  },
 (error) => Promise.reject(error)
);

instance.interceptors.response.use(
 (response) => response,
 (error) => {
    const status = error?.response?.status;
    const data = error?.response?.data;
    const method = (error?.config?.method || '').toLowerCase();

    if (status === 404 && method === 'get') {
      return Promise.reject(error);
    }

    const message = (data?.message || data?.error || '').toString().toLowerCase();
    const looksLikeEmpty = message.includes('no ') && message.includes('found');

    if (status === 401) {
      clearStoredAuth();
      toast.error('Session expired. Please login again.');
    } else if (status >= 500) {
      toast.error('Server error. Please try again later.');
    } else if (status >= 400) {
      if (!looksLikeEmpty) {
        toast.error(data?.message || data?.error || 'Request failed.');
      }
    } else if (!error.response) {
      toast.error('Network error. Check your connection.');
    }

    return Promise.reject(error);
  }
);

export default instance;

export const getAuthToken = () => {
  const { token } = getActiveToken();
  return token;
};

